import axios from 'axios';
import { Pool, PoolClient, QueryResult, QueryResultRow } from 'pg';

// Import ValidationError from utils/errors
import { ValidationError } from '../utils/errors';

// Environment detection
const isBrowser = typeof window !== 'undefined';

// Base service that works in both browser and Node environments
export class BaseService {
  protected pool: Pool | null = null;

  constructor() {
    // Initialize the pool only in server environment
    if (!isBrowser) {
      this.initializePool();
    }
  }

  private initializePool() {
    try {
      // Only import pg in server environment
      const { Pool } = require('pg');
      this.pool = new Pool({
        connectionString: process.env.DATABASE_URL,
        ssl: process.env.NODE_ENV === 'production' ? { rejectUnauthorized: false } : false
      });
    } catch (error) {
      console.warn('Could not initialize database pool. This is expected in browser environment.');
    }
  }

  protected async executeQuery<T extends QueryResultRow = any>(
    query: string,
    params: any[] = []
  ): Promise<QueryResult<T>> {
    if (isBrowser) {
      // In browser environment, use API endpoints instead of direct DB access
      try {
        const response = await axios.post('/api/db/query', {
          query,
          params
        });
        return response.data;
      } catch (error) {
        console.error('Database query error:', error);
        throw error;
      }
    } else {
      // In Node.js environment, use pg directly
      try {
        if (!this.pool) {
          this.initializePool();
        }
        
        return await this.pool!.query<T>(query, params);
      } catch (error) {
        console.error('Database query error:', error);
        throw error;
      }
    }
  }

  protected async withTransaction<T>(
    callback: (client: PoolClient) => Promise<T>
  ): Promise<T> {
    if (isBrowser) {
      throw new Error('Transactions are not supported in browser environment');
    }
    
    if (!this.pool) {
      this.initializePool();
    }
    
    const client = await this.pool!.connect();
    try {
      await client.query('BEGIN');
      const result = await callback(client);
      await client.query('COMMIT');
      return result;
    } catch (error) {
      await client.query('ROLLBACK');
      throw error;
    } finally {
      client.release();
    }
  }

  protected async beginTransaction(): Promise<PoolClient | null> {
    if (isBrowser) {
      return null;
    }
    
    if (!this.pool) {
      this.initializePool();
    }
    
    const client = await this.pool!.connect();
    await client.query('BEGIN');
    return client;
  }

  protected async commitTransaction(client: PoolClient | null): Promise<void> {
    if (client) {
      await client.query('COMMIT');
      client.release();
    }
  }

  protected async rollbackTransaction(client: PoolClient | null): Promise<void> {
    if (client) {
      await client.query('ROLLBACK');
      client.release();
    }
  }

  protected validateRequired(data: any, fields: string[]) {
    const missing = fields.filter(field => !data[field]);
    if (missing.length > 0) {
      throw new ValidationError(
        `Missing required fields: ${missing.join(', ')}`
      );
    }
  }

  protected formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }
  
  protected formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  }
} 